import { entries, get, isPlainObject, noop, omit, values } from 'lodash-es';
import axios from 'axios';
import lf from 'lovefield';
import { nanoid } from 'nanoid';
import * as Comlink from 'comlink';

import { formatEdmDateTime, formatEdmTime } from '../date';
import { PRIVACY_CONTROLS_MODES } from '../constants';
import { ODATA_DATA_TYPES } from '../odata/utils';
import { dataflowApiBase, getServiceInstance } from '../service';
import { WORKER_FETCH_STATE, generateDatabaseNamePrefix, initialWorkerState } from './utils';
import { LOG_TYPE } from 'components/ReportViewComponents/constants';

const LOVEFIELD_DATA_TYPES = {
  [ODATA_DATA_TYPES['Edm.Binary']]: lf.Type.STRING,
  [ODATA_DATA_TYPES['Edm.Boolean']]: lf.Type.BOOLEAN,
  [ODATA_DATA_TYPES['Edm.Byte']]: lf.Type.NUMBER,
  [ODATA_DATA_TYPES['Edm.DateTime']]: lf.Type.DATE_TIME,
  [ODATA_DATA_TYPES['Edm.DateTimeOffset']]: lf.Type.DATE_TIME,
  [ODATA_DATA_TYPES['Edm.Decimal']]: lf.Type.NUMBER,
  [ODATA_DATA_TYPES['Edm.Double']]: lf.Type.NUMBER,
  [ODATA_DATA_TYPES['Edm.Guid']]: lf.Type.STRING,
  [ODATA_DATA_TYPES['Edm.Int16']]: lf.Type.INTEGER,
  [ODATA_DATA_TYPES['Edm.Int32']]: lf.Type.INTEGER,
  [ODATA_DATA_TYPES['Edm.Int64']]: lf.Type.INTEGER,
  [ODATA_DATA_TYPES['Edm.SByte']]: lf.Type.NUMBER,
  [ODATA_DATA_TYPES['Edm.Single']]: lf.Type.NUMBER,
  [ODATA_DATA_TYPES['Edm.String']]: lf.Type.STRING,
  [ODATA_DATA_TYPES['Edm.Time']]: lf.Type.DATE_TIME,
};

let dbInstance;
let schemaSnapshot;
let rootPrimaryKeys = [];
let propertiesWithDateTimeDataType = {};
let propertiesWithIntegerOrFloatDataType = [];
let propertiesWithBooleanDataType = {};
let isFetchCancelled = false;

let sharedRequestCancelTokenSourceList = [];

export async function cancelPendingRequests(reason) {
  sharedRequestCancelTokenSourceList.forEach((source) => source?.cancel(reason));
  sharedRequestCancelTokenSourceList = [];
  isFetchCancelled = true;
}

export async function fetchRecordsCount({ connectionId, queryUrl, tenantId, authToken }) {
  const tokenSource = axios.CancelToken.source();
  sharedRequestCancelTokenSourceList.push(tokenSource);

  const $countUrl = createNewUrlInstance(queryUrl);
  $countUrl.searchParams.delete('$expand');
  $countUrl.pathname = $countUrl.pathname.concat('/$count');

  let recordsCount = 0;
  try {
    recordsCount = await getServiceInstance(tenantId, { isWorker: true, authToken }).post(
      `${dataflowApiBase}/connection/${connectionId}/proxy`,
      {
        skipLog: true,
        url: $countUrl,
        type: 'xml',
      },
      {
        cancelToken: tokenSource.token,
      }
    );
  } catch (error) {
    if (axios.isCancel(error)) {
      return error;
    }
  }

  return recordsCount;
}

const buildWorkerState = () => {
  let state = { ...initialWorkerState };

  return {
    actions: {
      reset: () => {
        state = { ...initialWorkerState };
      },
      setFetchState: (fetchState) => {
        state.changeId = nanoid();
        state.fetchState = fetchState;
      },
      setTotalRecords: (totalRecords) => {
        state.changeId = nanoid();
        state.totalRecords = totalRecords;
      },
      setRecordsFetched: (count) => {
        state.changeId = nanoid();
        state.recordsFetched = count;
      },
    },
    state,
  };
};

let workerState = buildWorkerState();

export const getWorkerFetchState = () => workerState.state;

export async function createDBInstance({
  dbName,
  entity,
  expandedNavigationProps,
  expandOptionsMap,
  schema,
  recordsCount,
  connectionId,
  tenantId,
  queryUrl,
  tabId,
  authToken,
  showPicklistValues,
  selectedSensitiveMode,
}) {
  try {
    await purgeIDB({ tabId });

    bustCache();

    schemaSnapshot = schema;
    rootPrimaryKeys = entity.property.filter((prop) => prop.isKey);

    const schemaBuilder = lf.schema.create(dbName, 1);

    const entitySelector = `${entity.namespace}.${entity.name}`;
    const entityType = schema.entityTypesMap[entitySelector];

    createTables({
      tableName: entityType.name,
      schemaBuilder,
      entityType,
      expandedNavigationProps,
      expandOptionsMap,
    });

    return schemaBuilder.connect().then(async (db) => {
      dbInstance = db;
      isFetchCancelled = false;

      workerState = buildWorkerState();
      workerState.actions.setTotalRecords(recordsCount);
      workerState.actions.setFetchState(WORKER_FETCH_STATE.FETCHING);

      const cancelTokenSource = axios.CancelToken.source();
      sharedRequestCancelTokenSourceList.push(cancelTokenSource);

      const url = createNewUrlInstance(queryUrl);
      url.searchParams.set('$format', 'json');

      try {
        const response = await getServiceInstance(tenantId, { isWorker: true, authToken }).post(
          `${dataflowApiBase}/connection/${connectionId}/proxy`,
          {
            url: url.toString(),
            type: LOG_TYPE.REPORT_VIEW,
            additional_info: {
              show_picklist_value: showPicklistValues,
              should_mask_data: selectedSensitiveMode === PRIVACY_CONTROLS_MODES.MASKING.id,
              should_anonymize_data:
                selectedSensitiveMode === PRIVACY_CONTROLS_MODES.ANONYMIZATION.id,
            },
          },
          {
            cancelToken: cancelTokenSource.token,
          }
        );

        if (!response || !response?.d) {
          workerState.actions.setFetchState(WORKER_FETCH_STATE.FETCH_FAILED);
          throw new Error('Failed to fetch data');
        }

        workerState.actions.setFetchState(WORKER_FETCH_STATE.IMPORTING);

        let rawRecords = [];
        if (Array.isArray(response.d)) {
          rawRecords = response.d;
        }

        if (Array.isArray(response.d?.results)) {
          rawRecords = response.d?.results;
        }

        workerState.actions.setRecordsFetched(rawRecords.length);

        if (!rawRecords.length) {
          workerState.actions.setFetchState(WORKER_FETCH_STATE.READY);

          if (isFetchCancelled) {
            return isFetchCancelled;
          }

          return;
        }

        try {
          await importToDatabase(entity, rawRecords);
        } catch (error) {
          workerState.actions.setFetchState(WORKER_FETCH_STATE.IMPORT_FAILED);

          throw new Error(error);
        }
        workerState.actions.setFetchState(WORKER_FETCH_STATE.READY);
      } catch (error) {
        workerState.actions.setFetchState(WORKER_FETCH_STATE.FETCH_FAILED);

        if (error instanceof Error) {
          throw new Error(error.message);
        }

        const { error: _error } = error;

        if (_error) {
          throw new Error(JSON.stringify(_error));
        }

        throw new Error(JSON.stringify(error));
      }
    });
  } catch (error) {
    console.error(`Failed to create database: ${dbName}`, error);
  }
}

async function importToDatabase(entity, rawRecords) {
  let nestedTables = {};
  const records = rawRecords.map((record) => {
    const flattenedRecord = {};
    flattenRecord(omit(record, '__metadata'), flattenedRecord);

    return flattenedRecord;
  });

  const pushDataToTable = async ({
    tableName,
    entity,
    records,
    isNested = false,
    parentTableName = '',
  }) => {
    const _tableName = isNested
      ? getTableName(`nested_${parentTableName}_${tableName}`)
      : tableName;

    const table = dbInstance.getSchema().table(_tableName);
    const rows = [];

    if (!isNested) {
      records.forEach((row) => {
        const primaryKeys = rootPrimaryKeys.reduce((acc, key) => {
          return {
            ...acc,
            [key.name]: row[key.name],
          };
        }, {});

        const pk_keyHash = JSON.stringify(primaryKeys);

        const _record = omit(row, '__metadata');

        const formattedBooleanProperties = entries(propertiesWithBooleanDataType).reduce(
          (acc, [colName, prop]) => {
            const originalColumnName = colName.replace(LF_COL_PREFIX, '');
            let boolValue = get(_record, originalColumnName);
            if (boolValue === false) {
              boolValue = 'No';
            } else if (boolValue === true) {
              boolValue = 'Yes';
            }

            return { ...acc, [colName]: boolValue };
          },
          {}
        );

        const formattedDateProperties = entries(propertiesWithDateTimeDataType).reduce(
          (acc, [colName, prop]) => {
            const originalColumnName = colName.replace(LF_COL_PREFIX, '');
            const dateString = get(_record, originalColumnName);
            const formattedDate = getFormattedDateTimeValue(dateString, prop.type);

            return { ...acc, [colName]: formattedDate };
          },
          {}
        );
        const formattedIntegerOrFloatProperties = propertiesWithIntegerOrFloatDataType.reduce(
          (acc, colName) => {
            const originalColumnName = colName.replace(LF_COL_PREFIX, '');
            const value = get(_record, originalColumnName);

            return { ...acc, [colName]: Number(value) };
          },
          {}
        );

        const prefixedRecord = entries(_record).reduce(
          (acc, [key, value]) => ({ ...acc, [getLfColName(key)]: value }),
          {}
        );

        const rowData = {
          ...prefixedRecord,
          ...formattedDateProperties,
          ...formattedIntegerOrFloatProperties,
          ...formattedBooleanProperties,
          _INT_UQ_ID: nanoid(),
          lf_dataflow_keyHash: pk_keyHash,
        };

        rows.push(table.createRow(rowData));

        entries(row).forEach(([propertyName, propertyData]) => {
          if (Array.isArray(propertyData)) {
            const currentNavProp = entity.navigationProperty.find(
              (navProp) => navProp.name === propertyName
            );

            nestedTables = {
              ...nestedTables,
              [propertyName]: {
                tableName: currentNavProp.name,
                parentTableName: _tableName,
                isNested: true,
                data: [
                  ...(nestedTables[propertyName] ? nestedTables[propertyName].data : []),
                  {
                    records: propertyData,
                    lf_dataflow_fk_keyHash: pk_keyHash,
                  },
                ],
              },
            };
          }
        });
      });
    } else {
      records.forEach((row) => {
        row.records.forEach((record) => {
          const _record = omit(record, '__metadata');

          const formattedBooleanProperties = entries(propertiesWithBooleanDataType).reduce(
            (acc, [colName, prop]) => {
              const originalColumnName = colName.replace(LF_COL_PREFIX, '');
              let boolValue = get(_record, originalColumnName);
              if (boolValue === false) {
                boolValue = 'No';
              } else if (boolValue === true) {
                boolValue = 'Yes';
              }

              return { ...acc, [colName]: boolValue };
            },
            {}
          );

          const formattedDateProperties = entries(propertiesWithDateTimeDataType).reduce(
            (acc, [colName, prop]) => {
              const originalColumnName = colName.replace(LF_COL_PREFIX, '');
              const dateString = get(_record, originalColumnName);
              if (dateString) {
                const formattedDate = getFormattedDateTimeValue(dateString, prop.type);
                return { ...acc, [colName]: formattedDate };
              } else {
                return acc;
              }
            },
            {}
          );

          const formattedIntegerOrFloatProperties = propertiesWithIntegerOrFloatDataType.reduce(
            (acc, colName) => {
              const originalColumnName = colName.replace(LF_COL_PREFIX, '');
              const value = get(_record, originalColumnName);

              return { ...acc, [colName]: Number(value) };
            },
            {}
          );

          const prefixedRecord = entries(_record).reduce(
            (acc, [key, value]) => ({ ...acc, [getLfColName(key)]: value }),
            {}
          );

          const rowData = {
            ...prefixedRecord,
            ...formattedDateProperties,
            ...formattedIntegerOrFloatProperties,
            ...formattedBooleanProperties,
            _INT_UQ_ID: nanoid(),
            [`${parentTableName}_lf_dataflow_keyHash`]: row.lf_dataflow_fk_keyHash,
          };

          rows.push(table.createRow(rowData));
        });
      });
    }

    await dbInstance.insertOrReplace().into(table).values(rows).exec();
  };

  await pushDataToTable({ tableName: entity.name, entity, records });

  await Promise.all(
    values(nestedTables).map(
      async (nestedTable) =>
        await pushDataToTable({
          tableName: nestedTable.tableName,
          records: nestedTable.data,
          isNested: nestedTable.isNested,
          parentTableName: nestedTable.parentTableName,
        })
    )
  );
}

function createDataQuery(parsedState) {
  const selectedEntity = parsedState.entity.value.name;
  const selectedTable = dbInstance.getSchema().table(selectedEntity);
  const expand = parsedState.expand;

  const query = dbInstance.select().from(selectedTable);

  // When report view is open, we don't want to do the LEFT OUTER JOIN.
  if (expand.value.length > 0) {
    expand.value.forEach((propertyName) => {
      const propertyData = expand.optionsMap[propertyName];
      if (propertyData.multiplicity === '*') {
        const nestedTable = getNestedTableInstance(propertyData.name, selectedEntity);

        query.leftOuterJoin(
          nestedTable,
          selectedTable.lf_dataflow_keyHash.eq(nestedTable[`${selectedEntity}_lf_dataflow_keyHash`])
        );
      }
    });
  }

  return query;
}

const createCountQuery = (parsedState) => {
  const selectedEntity = parsedState.entity.value.name;
  const expand = parsedState.expand;
  const selectedTable = dbInstance.getSchema().table(selectedEntity);
  const query = dbInstance.select(selectedTable.lf_dataflow_keyHash).from(selectedTable);

  // Expand is required in createCountQuery incase a filter based on the n-multiplicity property is added
  if (expand.value.length > 0) {
    expand.value.forEach((propertyName) => {
      const propertyData = expand.optionsMap[propertyName];
      if (propertyData.multiplicity === '*') {
        const nestedTable = getNestedTableInstance(propertyData.name, selectedEntity);

        query.leftOuterJoin(
          nestedTable,
          selectedTable.lf_dataflow_keyHash.eq(nestedTable[`${selectedEntity}_lf_dataflow_keyHash`])
        );
      }
    });
  }

  return query;
};

/**
 * Strips LF_COL_PREFIX from object keys. For join results (n-multiplicity),
 * Lovefield returns nested objects per table; we must strip the prefix
 * inside those nested objects too so downstream code sees e.g. "Id" not "lf_col_Id".
 */
const stripLfColPrefixFromRow = (row) => {
  return entries(row).reduce((acc, [key, value]) => {
    const strippedKey = key.startsWith(LF_COL_PREFIX) ? key.slice(LF_COL_PREFIX.length) : key;
    const strippedValue =
      isPlainObject(value) && !Array.isArray(value) ? stripLfColPrefixFromRow(value) : value;
    acc[strippedKey] = strippedValue;
    return acc;
  }, {});
};

/**
 * For join results, nested table rows need _INT_navName set to the nav prop name
 * (e.g. "Orders") so ReportView can group by expand. Table key is "nested_Root_NavProp".
 */
function setNavNameOnNestedRows(queryData, parsedState) {
  const expand = parsedState.expand;
  if (!expand?.value?.length) return;
  const rootTableName = parsedState.entity?.value?.name;
  if (!rootTableName) return;

  queryData.forEach((row) => {
    entries(row).forEach(([tableKey, value]) => {
      if (!tableKey.includes('nested_') || !isPlainObject(value)) return;
      const navName = expand.value.find(
        (name) => getTableName(`nested_${rootTableName}_${name}`) === tableKey
      );
      if (navName) {
        value._INT_navName = navName;
      }
    });
  });
}

export async function runPagination(limit, skip, currentState) {
  const parsedState = JSON.parse(currentState);

  const dataQuery = createDataQuery(parsedState);
  const countQuery = createCountQuery(parsedState);

  // Get a transaction object first.
  var txn = dbInstance.createTransaction();

  // exec in order: query 1 first then query2, guaranteed snapshot
  const [rawQueryData, countData] = await txn.exec([dataQuery.limit(limit).skip(skip), countQuery]);
  const queryData = rawQueryData.map(stripLfColPrefixFromRow);

  // Join results: set _INT_navName on nested rows and return expand list for ReportView
  if (parsedState.expand?.value?.length > 0) {
    setNavNameOnNestedRows(queryData, parsedState);
  }

  return {
    queryData,
    totalCount: countData.length,
    expandedPropertyNames: parsedState.expand?.value ?? [],
  };
}

// Helper Functions
const bustCache = () => {
  schemaSnapshot = {};
  rootPrimaryKeys = [];
  propertiesWithDateTimeDataType = {};
  propertiesWithIntegerOrFloatDataType = [];
};

function deleteDB(dbName) {
  return new Promise((resolve, reject) => {
    const dbDeleteRequest = indexedDB.deleteDatabase(dbName);
    dbDeleteRequest.onerror = () => {
      reject(`Failed to delete database: ${dbName}`);
    };
    dbDeleteRequest.onsuccess = () => {
      resolve(`Deleted database ${dbName}`);
    };
    dbDeleteRequest.onblocked = () => {
      reject(`Failed to delete database: ${dbName}`);
    };
  });
}

export async function purgeIDB({ tabId }) {
  if (dbInstance) {
    dbInstance.close();
  }
  const databases = await indexedDB.databases();
  await Promise.all(
    databases.map(async (db) => {
      if (db.name.startsWith(generateDatabaseNamePrefix({ tabId }))) {
        await deleteDB(db.name);
      }
    })
  ).catch(noop);
}

const createNewUrlInstance = (_url) => {
  const url = new URL(_url);
  url.searchParams.delete('$format');
  url.searchParams.delete('$inlinecount');
  url.searchParams.delete('paging');

  return url;
};

const getTableName = (nameString) => nameString.replace('.', '_');

const LF_COL_PREFIX = 'lf_col_';
const getLfColName = (name) => `${LF_COL_PREFIX}${name}`;

const createTables = ({
  tableName,
  schemaBuilder,
  entityType,
  isNested = false,
  parentTableName = '',
  expandedNavigationProps = [],
  expandOptionsMap = {},
}) => {
  const primaryKeys = ['_INT_UQ_ID'];
  const nullableProperties = [];

  const _tableName = isNested ? getTableName(`nested_${parentTableName}_${tableName}`) : tableName;

  const tableBuilder = schemaBuilder
    .createTable(_tableName)
    .addColumn(
      isNested ? `${parentTableName}_lf_dataflow_keyHash` : 'lf_dataflow_keyHash',
      LOVEFIELD_DATA_TYPES['Edm.String']
    )
    .addColumn('_INT_UQ_ID', LOVEFIELD_DATA_TYPES['Edm.Int16']);

  entityType.property.forEach((prop) => {
    const colName = getLfColName(prop.name);
    if (prop.isKey) {
      primaryKeys.push(colName);
    } else if (prop.nullable) {
      nullableProperties.push(colName);
    }

    tableBuilder.addColumn(colName, LOVEFIELD_DATA_TYPES[prop.type]);

    if (
      prop.type === ODATA_DATA_TYPES['Edm.DateTime'] ||
      prop.type === ODATA_DATA_TYPES['Edm.DateTimeOffset'] ||
      prop.type === ODATA_DATA_TYPES['Edm.Time']
    ) {
      propertiesWithDateTimeDataType = {
        ...propertiesWithDateTimeDataType,
        [colName]: prop,
      };
    } else if (
      prop.type === ODATA_DATA_TYPES['Edm.Decimal'] ||
      prop.type === ODATA_DATA_TYPES['Edm.Double'] ||
      prop.type === ODATA_DATA_TYPES['Edm.Int16'] ||
      prop.type === ODATA_DATA_TYPES['Edm.Int32'] ||
      prop.type === ODATA_DATA_TYPES['Edm.Int64']
    ) {
      propertiesWithIntegerOrFloatDataType.push(colName);
    } else if (prop.type === ODATA_DATA_TYPES['Edm.Boolean']) {
      propertiesWithBooleanDataType = {
        ...propertiesWithBooleanDataType,
        [colName]: prop,
      };
    }
  });

  if (!isNested) {
    expandedNavigationProps.forEach((navProp) => {
      const navigationProperty = expandOptionsMap[navProp];

      const association = schemaSnapshot.associationsMap[navigationProperty.relationship];
      const toRole = association.end.find(({ role }) => role === navigationProperty.toRole);

      const expandedEnity = schemaSnapshot.entityTypesMap[toRole.type];

      // Create seperate table for each N-multiplicity property
      if (navigationProperty.multiplicity === '*') {
        createTables({
          tableName: navigationProperty.name,
          schemaBuilder,
          entityType: expandedEnity,
          isNested: true,
          parentTableName: entityType.name,
        });
      }
      // Flatten out the 1-multiplicity property
      else {
        expandedEnity.property.forEach((prop) => {
          const flatColName = `${navigationProperty.name}_${prop.name}`;
          const colName = getLfColName(flatColName);
          if (prop.nullable) {
            nullableProperties.push(colName);
          }

          tableBuilder.addColumn(colName, LOVEFIELD_DATA_TYPES[prop.type]);

          if (
            prop.type === ODATA_DATA_TYPES['Edm.DateTime'] ||
            prop.type === ODATA_DATA_TYPES['Edm.DateTimeOffset'] ||
            prop.type === ODATA_DATA_TYPES['Edm.Time']
          ) {
            propertiesWithDateTimeDataType = {
              ...propertiesWithDateTimeDataType,
              [colName]: prop,
            };
          } else if (
            prop.type === ODATA_DATA_TYPES['Edm.Decimal'] ||
            prop.type === ODATA_DATA_TYPES['Edm.Double'] ||
            prop.type === ODATA_DATA_TYPES['Edm.Int16'] ||
            prop.type === ODATA_DATA_TYPES['Edm.Int32'] ||
            prop.type === ODATA_DATA_TYPES['Edm.Int64']
          ) {
            propertiesWithIntegerOrFloatDataType.push(colName);
          } else if (prop.type === ODATA_DATA_TYPES['Edm.Boolean']) {
            propertiesWithBooleanDataType = {
              ...propertiesWithBooleanDataType,
              [colName]: prop,
            };
          }
        });
      }
    });

    tableBuilder.addUnique('uq_lf_dataflow_keyHash', ['lf_dataflow_keyHash']);
  } else {
    tableBuilder.addForeignKey(`lf_dataflow_fk_keyHash`, {
      local: `${parentTableName}_lf_dataflow_keyHash`,
      ref: `${parentTableName}.lf_dataflow_keyHash`,
    });
  }

  tableBuilder.addPrimaryKey(primaryKeys).addNullable(nullableProperties);
};

function flattenRecord(record, rootObject, keyPrefix = '') {
  entries(record).forEach(([key, value]) => {
    if (isPlainObject(value)) {
      if (value.__metadata) {
        flattenRecord(omit(value, '__metadata'), rootObject, `${key}_`);
      } else if (keyPrefix === '' && value.results && Array.isArray(value.results)) {
        rootObject[`${keyPrefix}${key}`] = value.results;
      }
    } else {
      rootObject[`${keyPrefix}${key}`] = value;
    }
  });
}

const getFormattedDateTimeValue = (dateString, type) => {
  if (
    type === ODATA_DATA_TYPES['Edm.DateTime'] ||
    type === ODATA_DATA_TYPES['Edm.DateTimeOffset']
  ) {
    return dateString ? formatEdmDateTime({ dateTime: dateString, returnObj: true }) : null;
  } else if (type === ODATA_DATA_TYPES['Edm.Time']) {
    return dateString ? formatEdmTime({ time: dateString, returnObj: true }) : null;
  }
};

const getNestedTableInstance = (tableName, rootTableName) => {
  return dbInstance.getSchema().table(getTableName(`nested_${rootTableName}_${tableName}`));
};

const getFilteredQuery = ({ rule, selectedTable, expand, rootTableName }) => {
  let propertyName = rule.property.name;
  let _selectedTable = selectedTable;

  // If the query is against an expanded n-multiplicty navProperty, then we change the selectTable and the propertyName
  if (propertyName.includes('/')) {
    const [nestedTableName, columnName] = propertyName.split('/');
    const propertyData = expand.optionsMap[nestedTableName];
    if (propertyData.multiplicity === '*') {
      _selectedTable = getNestedTableInstance(propertyData.name, rootTableName);
      propertyName = getLfColName(columnName);
    } else {
      propertyName = getLfColName(`${nestedTableName}_${columnName}`);
    }
  } else {
    propertyName = getLfColName(propertyName);
  }

  if (!_selectedTable[propertyName]) {
    return;
  }

  const filter = rule.filter.key;
  const param = rule.param;
  const type = rule.property.type;
  let regx = '';

  switch (filter) {
    case 'isTrue': {
      return _selectedTable[propertyName].eq(true);
    }
    case 'isFalse': {
      return _selectedTable[propertyName].eq(false);
    }
    case 'eq': {
      return _selectedTable[propertyName].eq(param);
    }
    case 'ne': {
      return _selectedTable[propertyName].neq(param);
    }
    case 'gt': {
      return _selectedTable[propertyName].gt(param);
    }
    case 'ge': {
      return _selectedTable[propertyName].gte(param);
    }
    case 'lt': {
      return _selectedTable[propertyName].lt(param);
    }
    case 'le': {
      return _selectedTable[propertyName].lte(param);
    }
    case 'in': {
      const _param = param.split(';').map((value) => {
        if (type === ODATA_DATA_TYPES['Edm.String']) {
          return value;
        }

        return Number(value);
      });

      return _selectedTable[propertyName].in(_param);
    }
    case 'roundEq': {
      const lowerBound = param - 0.5;
      const upperBound = param + 0.5;

      return lf.op.and(
        _selectedTable[propertyName].gte(lowerBound),
        _selectedTable[propertyName].lt(upperBound)
      );
    }
    case 'floorEq': {
      const lowerBound = param;
      const upperBound = param + 1;

      return lf.op.and(
        _selectedTable[propertyName].gte(lowerBound),
        _selectedTable[propertyName].lt(upperBound)
      );
    }
    case 'ceilingEq': {
      const lowerBound = param - 1;
      const upperBound = param;

      return lf.op.and(
        _selectedTable[propertyName].gt(lowerBound),
        _selectedTable[propertyName].lte(upperBound)
      );
    }

    case 'caseInsensitiveEq': {
      regx = new RegExp(`${param}\\b`, 'gi');
      return _selectedTable[propertyName].match(regx);
    }
    case 'caseInsensitiveNe': {
      regx = new RegExp(`[^${param}]`, 'gi');
      return _selectedTable[propertyName].match(regx);
    }
    case 'startsWith': {
      regx = new RegExp(`^${param}`, 'g');
      return _selectedTable[propertyName].match(regx);
    }
    case 'notStartsWith': {
      regx = new RegExp(`^[^${param}]`, 'g');
      return _selectedTable[propertyName].match(regx);
    }
    case 'endsWith': {
      regx = new RegExp(`${param}$`, 'g');
      return _selectedTable[propertyName].match(regx);
    }
    case 'notEndsWith': {
      regx = new RegExp(`[^${param}]$`, 'g');
      return _selectedTable[propertyName].match(regx);
    }
    case 'contains': {
      regx = new RegExp(`${param}`, 'g');
      return _selectedTable[propertyName].match(regx);
    }
    case 'notContains': {
      regx = new RegExp(`^((?!${param}).)*$`, 'g');
      return _selectedTable[propertyName].match(regx);
    }
    case 'datetimeEq': {
      const utcDate = formatEdmDateTime({ dateTime: param, returnObj: true });
      return _selectedTable[propertyName].eq(utcDate);
    }
    case 'datetimeNe': {
      const utcDate = formatEdmDateTime({ dateTime: param, returnObj: true });
      return _selectedTable[propertyName].neq(utcDate);
    }
    case 'datetimeEqOrBefore': {
      const utcDate = formatEdmDateTime({ dateTime: param, returnObj: true });
      return _selectedTable[propertyName].lte(utcDate);
    }
    case 'datetimeBefore': {
      const utcDate = formatEdmDateTime({ dateTime: param, returnObj: true });
      return _selectedTable[propertyName].lt(utcDate);
    }
    case 'datetimeAfter': {
      const utcDate = formatEdmDateTime({ dateTime: param, returnObj: true });
      return _selectedTable[propertyName].gt(utcDate);
    }
    case 'datetimeEqOrAfter': {
      const utcDate = formatEdmDateTime({ dateTime: param, returnObj: true });
      return _selectedTable[propertyName].gte(utcDate);
    }
    case 'timeEq': {
      const utcDate = formatEdmDateTime({ dateTime: param, returnObj: true });
      return _selectedTable[propertyName].eq(utcDate);
    }
    case 'timeNe': {
      const utcDate = formatEdmDateTime({ dateTime: param, returnObj: true });
      return _selectedTable[propertyName].neq(utcDate);
    }
    case 'timeEqOrBefore': {
      const utcDate = formatEdmDateTime({ dateTime: param, returnObj: true });
      return _selectedTable[propertyName].lte(utcDate);
    }
    case 'timeBefore': {
      const utcDate = formatEdmDateTime({ dateTime: param, returnObj: true });
      return _selectedTable[propertyName].lt(utcDate);
    }
    case 'timeAfter': {
      const utcDate = formatEdmDateTime({ dateTime: param, returnObj: true });
      return _selectedTable[propertyName].gt(utcDate);
    }
    case 'timeEqOrAfter': {
      const utcDate = formatEdmDateTime({ dateTime: param, returnObj: true });
      return _selectedTable[propertyName].gte(utcDate);
    }

    default: {
      return;
    }
  }
};

Comlink.expose({
  cancelPendingRequests,
  fetchRecordsCount,
  createDBInstance,
  runPagination,
  getWorkerFetchState,
});
